import { Tooltip } from "@/components/ui/Tooltip";
import { formatMoney, type CustomerInsights, type LoyaltyLevel } from "./productResolver";

interface Props {
    insights: CustomerInsights;
    now: number;
}

const humanizeDays = (days: number): string => {
    if (days < 1) return "today";
    if (days < 30) return `${days}d`;
    if (days < 365) return `${Math.round(days / 30)}mo`;
    const years = Math.floor(days / 365);
    const months = Math.round((days % 365) / 30);
    return months > 0 ? `${years}y ${months}mo` : `${years}y`;
};

const LOYALTY_CLS: Record<LoyaltyLevel, string> = {
    New: "bg-gray-100 text-gray-700",
    Regular: "bg-blue-100 text-blue-800",
    Loyal: "bg-purple-100 text-purple-800",
    Champion: "bg-amber-100 text-amber-800",
};

const Tile: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
    <div className="flex min-w-[7rem] flex-col gap-0.5 px-3 py-1.5">
        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</span>
        {hint ? (
            <Tooltip content={hint}>
                <span className="cursor-help text-sm font-semibold text-gray-900">{children}</span>
            </Tooltip>
        ) : (
            <span className="text-sm font-semibold text-gray-900">{children}</span>
        )}
    </div>
);

export const CustomerInsightsStrip: React.FC<Props> = ({ insights, now }) => {
    const { tenureDays, paidCount, totalByCurrency, pricedCount, loyalty, active, churnedAt, trend } = insights;

    // Nothing meaningful to show for a never-paid user.
    if (paidCount === 0 && tenureDays == null) return null;

    const currencies = Object.keys(totalByCurrency);
    const totalText =
        currencies.length === 0
            ? "—"
            : currencies.map((c) => formatMoney(totalByCurrency[c], c)).join(" + ");
    const coverage = pricedCount < paidCount ? ` (${pricedCount}/${paidCount} priced)` : "";

    const trendMeta =
        trend === "upgrade"
            ? { icon: "↑", text: "Upgraded", cls: "text-green-700" }
            : trend === "downgrade"
            ? { icon: "↓", text: "Downgraded", cls: "text-amber-700" }
            : trend === "stable"
            ? { icon: "→", text: "Stable", cls: "text-gray-600" }
            : null;

    return (
        <div className="flex flex-wrap items-stretch divide-x divide-gray-100 rounded-lg border border-gray-200 bg-gray-50/60">
            {tenureDays != null && (
                <Tile label="Customer since" hint="Time since first store purchase">
                    {humanizeDays(tenureDays)}
                </Tile>
            )}

            <Tile label="Paid subs" hint="Paid store subscriptions (excludes free trials & grants)">
                {paidCount}
            </Tile>

            <Tile
                label="Total paid"
                hint={
                    pricedCount < paidCount
                        ? `Sum of receipt prices. Only ${pricedCount} of ${paidCount} paid receipts carry a price (older iOS receipts predate Apple's price field), so this is a lower bound.`
                        : "Sum of receipt prices across all paid subscriptions."
                }
            >
                {totalText}
                {coverage && <span className="ml-1 text-[10px] font-normal text-gray-400">{coverage}</span>}
            </Tile>

            <Tile label="Loyalty" hint="Derived from tenure + number of paid subscriptions">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${LOYALTY_CLS[loyalty]}`}>
                    {loyalty}
                </span>
            </Tile>

            <Tile label="Status">
                {active ? (
                    <span className="inline-flex items-center gap-1 text-green-700">
                        <span className="h-2 w-2 rounded-full bg-green-500" /> Active
                    </span>
                ) : churnedAt != null ? (
                    <Tooltip content={`Last subscription lapsed ${new Date(churnedAt).toLocaleDateString()}`}>
                        <span className="cursor-help text-gray-600">
                            Churned · {humanizeDays(Math.max(0, Math.floor((now - churnedAt) / 86_400_000)))} ago
                        </span>
                    </Tooltip>
                ) : (
                    <span className="text-gray-500">—</span>
                )}
            </Tile>

            {trendMeta && (
                <Tile label="Trend" hint="Latest vs previous paid price (same currency): upgrade / downgrade / stable">
                    <span className={`inline-flex items-center gap-1 ${trendMeta.cls}`}>
                        <span>{trendMeta.icon}</span> {trendMeta.text}
                    </span>
                </Tile>
            )}
        </div>
    );
};

export default CustomerInsightsStrip;
